import angular from 'angular'

import { VdFeatureManager } from '@vend/vend.js'
import { subtract, vn } from 'vend-number'
import * as salesResource from '../components/sale/sales-resource/sales-resource'
import { FEATURE_ADD_ECOM_CUSTOM_CHARGES_ROW } from '../constants'
import {
  getPaymentsTotal,
  isCompleted,
  isLegacyServiceSale,
  isReturn,
  isVoided,
} from '../functions/sale'
import { hasServiceOrder, isServiceStatusFinal } from '../functions/service'
import { RegisterManager } from '../managers/register-manager'
import { SaleSyncManager } from '../managers/sale-sync-manager'
import { ServicesManager } from '../managers/services-manager'
import { Sale } from '../models/sale'
import {
  ALLOW_RETURN_STATUSES,
  SaleStatus,
  SaleSyncStatus,
  ServiceActions,
  ServiceStatusProtobuf,
} from '../models/sale.constants'
import { $log, $stateParams } from '../ng-compat/imports'
import {
  calculateSaleDiscountPercentageAsDecimal,
  totalSurchargeFromPayments,
  totalTipFromAdjustments,
} from '../sale-calculation/calculate-sale'
import { validateQuoteAndHandleError } from '../util/quotes-utils'
import { ERROR_TYPES } from '../util/types'
import { SalesStore } from './database/sales-store'
import { SaleTransformer } from './sale-transformer'

/**
 * Service for loading sales from either IndexedDB or the API as appropriate and determining eligibility for use in the
 * sell screen (i.e. not voided, completed, etc).
 *
 * @module saleLoader
 */
angular
  .module('webregister.factories')
  .factory('saleLoader', SaleTransformerFactory)

class SaleLoadError extends Error {
  constructor(public type: string, message: string) {
    super(message)
  }
}

export type SaleLoader = ReturnType<typeof SaleTransformerFactory>

function SaleTransformerFactory(
  salesStore: SalesStore,
  saleTransformer: SaleTransformer,
  registerManager: RegisterManager,
  saleSyncManager: SaleSyncManager,
  servicesManager: ServicesManager,
  vdFeatureManager: VdFeatureManager
) {
  /**
   * Log the given message and return a rejected promise with the given error type and message.
   *
   * @private
   * @method logAndReject
   *
   * @param  type {String}
   *         The type of error that occurred
   *
   * @param  message {String}
   *         The error message
   *
   * @param  [logParams] {...Any}
   *         Additional params to log (e.g. the relevant sale or sale ID)
   *
   * @return {Promise} A rejected promise with the given type and message
   */
  function logAndReject(type: string, message: string, ...logParams: any[]) {
    $log.warn('[SaleLoader] ' + message, ...logParams)
    return Promise.reject(new SaleLoadError(type, message))
  }

  /**
   * Determine whether this sale can be returned or not. A return sale should be
   * completed or be awaiting dispatch.
   *
   * @return {Boolean} True if this sale is in a completed state, false
   * otherwise
   */
  function isReturnAllowed(status?: string): boolean {
    if (!status) {
      return false
    }

    return ALLOW_RETURN_STATUSES.includes(status as SaleStatus)
  }

  /**
   * Service for loading sales from either IndexedDB or the API as appropriate and determining eligibility for use in
   * the sell screen (i.e. not voided, completed, etc).
   *
   * @class SaleLoader
   * @static
   */
  return {
    /**
     * Load the sale with the given ID from IndexedDB or the API and determine whether the sale is appropriate to set
     * as the active sale. Checks to see if the sale exists, is not voided or completed.
     *
     * @method loadSale
     *
     * @param  {String} saleId
     *         The id (or localId) of the sale to load
     *
     * @return {Promise<Sale>} A promise resolved with the sale once it has been loaded and if it is
     *         appropriate to be set as the active sale. Rejected if the sale couldn't be found,
     *         was voided or completed.
     */
    loadSale(saleId: string) {
      return this._loadSale(saleId).then(sale => {
        if (isCompleted(sale.status) && sale.isSynced()) {
          return logAndReject(
            ERROR_TYPES.LOAD_SALE_ABORTED_COMPLETED,
            'Attempt to load completed sale, aborting.',
            sale
          )
        }

        if (!isReturn(sale)) {
          return sale
        }

        return this.loadReturnSale(sale.returnFor!).then(returnSale => {
          sale.originalSalePayments = returnSale.payments
          // when LSPay surcharge is enabled and the original sale has any surcharge,
          // we need to store the original surcharge amount and the net payment
          if (vdFeatureManager.get('lspay_surcharge')) {
            const originalSurchargeAmount = totalSurchargeFromPayments(
              returnSale.payments
            )
            const originalTipAmount = totalTipFromAdjustments(
              returnSale.adjustments
            )
            if (originalSurchargeAmount > 0) {
              sale.originalSurcharge = {
                amount: originalSurchargeAmount,
                netPaymentAmount: subtract(
                  getPaymentsTotal(returnSale.payments),
                  originalSurchargeAmount,
                  originalTipAmount
                ),
              }
            }
          }
          // Calculate the discount percentage for the return sale based on the line items from the original sale. We do this to ensure gift
          // cards are considered during the calculation (gift cards can not be returned and so will never be part of a return sale).
          sale.returnDiscountPercentage = calculateSaleDiscountPercentageAsDecimal(
            returnSale,
            registerManager.isTaxInclusive()
          )

          // sometimes the SourceId in the Sale object has same value for all charges,
          // we need to set it to the original sale's SourceId
          // This part should be removed when we fix the source_id issue
          if (
            vdFeatureManager.get(FEATURE_ADD_ECOM_CUSTOM_CHARGES_ROW) &&
            sale?.ecomCustomCharges &&
            !vn(sale.ecomCustomCharges.totalIncl).eq(0) &&
            returnSale?.ecomCustomCharges &&
            !vn(returnSale.ecomCustomCharges.totalIncl).eq(0)
          ) {
            const sourceIds = new Set(
              sale?.ecomCustomCharges.charges.map(charge => charge.sourceId)
            )

            // check if all sourceIds are the same in the sale
            if (sourceIds.size === 1) {
              sale.ecomCustomCharges = {
                total: returnSale.ecomCustomCharges.total * -1,
                totalIncl: returnSale?.ecomCustomCharges.totalIncl * -1,
                totalTax: returnSale?.ecomCustomCharges.totalTax * -1,
                charges:
                  returnSale?.ecomCustomCharges?.charges?.map(charge => ({
                    ...charge,
                    total: charge.total * -1,
                    totalIncl: charge.totalIncl * -1,
                    totalTax: charge.totalTax * -1,
                  })) || [],
              }
            }
          }

          return sale
        })
      })
    },

    /**
     * Loads a sale which can be returned. Currently used to store payments made on an original sale for a return.
     *
     * @method loadReturnSale
     *
     * @param {String} saleId
     *        The id of the sale to load
     *
     * @return {Promise<Sale>} A promise resolved with the sale once it has been loaded. Rejected if the sale
     *         couldn't be found, was voided or not confirmed.
     */
    loadReturnSale(saleId: string) {
      // If a sale is a return, get the latest version of the sale from the API.
      // This will be the most up to date version of the sale. For example if payments
      // were added to the sale from sales history these will be retrieved.
      return this._loadSale(saleId, true).then(sale => {
        if (isReturnAllowed(sale.status)) {
          return sale
        }

        return logAndReject(
          ERROR_TYPES.LOAD_SALE_ABORTED_COMPLETED,
          'Attempt to load a non return sale, aborting.',
          sale
        )
      })
    },

    /**
     * Load the sale with the given ID from IndexedDB or the API. Determines if the sale exists and is not voided.
     *
     * @private
     * @method _loadSale
     *
     * @param  {String} saleId
     *         The id (or localId) of the sale to load
     *
     * @param  {Boolean} loadFromApi
     *         Whether to force load the sale from the API rather than IndexedDB
     *
     * @return {Promise<Sale>} A promise resolved with the sale once it has been loaded. Rejected if the sale
     *         couldn't be found or was voided.
     */
    _loadSale(saleId: string, loadFromApi: boolean = false) {
      if (saleId === $stateParams.quoteId) {
        // these types are using only in this block
        const QUOTE_ERROR_TYPES = {
          ARCHIVED: 'archived',
          NO_CUSTOMER: 'no-customer',
          NO_PRODUCTS: 'no-products',
        }

        return saleSyncManager
          .getQuoteById($stateParams.quoteId)
          .then(quote => {
            const error = validateQuoteAndHandleError(quote, logAndReject)
            if (error) {
              return error
            }
            return saleTransformer.toSaleFromQuote(quote)
          })
          .catch(error => {
            switch (error.message) {
              case QUOTE_ERROR_TYPES.ARCHIVED:
                return logAndReject(
                  ERROR_TYPES.LOAD_QUOTE_ABORTED_ARCHIVED,
                  'The quote has been archived and cannot be converted to a sale.',
                  $stateParams.quoteId
                )
              case QUOTE_ERROR_TYPES.NO_CUSTOMER:
                return logAndReject(
                  ERROR_TYPES.LOAD_QUOTE_ABORTED_NO_CUSTOMER,
                  'The quote does not have a customer and cannot be converted to a sale',
                  $stateParams.quoteId
                )
              case QUOTE_ERROR_TYPES.NO_PRODUCTS:
                return logAndReject(
                  ERROR_TYPES.LOAD_QUOTE_ABORTED_NO_PRODUCTS,
                  'The quote has no products and cannot be converted to a sale',
                  $stateParams.quoteId
                )
              default:
                return logAndReject(
                  ERROR_TYPES.LOAD_QUOTE_ABORTED_NOT_FOUND,
                  'Unable to load quote',
                  $stateParams.quoteId
                )
            }
          })
      }
      return salesStore
        .loadSaleJson(saleId)
        .then(sale => {
          // If we find a sale in IndexedDB and its sync status is not UPLOADED then it likely means the
          // client copy of the sale is more up to date than the server (as there are unsynced changes in
          // IndexedDB) and so we load the client copy of the sale. If the status is UPLOADED then its
          // possible the server has a more up to date copy so request it from the API.
          if (
            !loadFromApi &&
            sale &&
            sale.syncStatus !== SaleSyncStatus.UPLOADED
          ) {
            return saleTransformer.toModel(sale)
          }

          return salesResource
            .get(saleId)
            .then(sale => saleTransformer.toModel(sale))
        })
        .then(sale => {
          if (sale && sale instanceof Sale) {
            if (isVoided(sale.status)) {
              return logAndReject(
                ERROR_TYPES.LOAD_SALE_ABORTED_VOIDED,
                'Attempt to load voided sale, aborting.',
                sale
              )
            }

            if (
              sale?.id &&
              (hasServiceOrder(sale.serviceFields, sale.customFields) ||
                isLegacyServiceSale(sale.status))
            ) {
              // change serviceAction to UPDATE_JOB to avoid creating a new job
              sale.serviceFields.serviceAction = ServiceActions.UPDATE_JOB
              // Get the serviceId when getting the agenda.
              // Then use the serviceId to get the service item.
              // Catch any errors and log them.
              let serviceId = ''

              return servicesManager
                .getServiceAgendaBySaleId(sale.id)
                .then(response => {
                  if (response) {
                    const { id, agenda, status } = response
                    if (status !== undefined) {
                      sale.serviceFields.status = status as ServiceStatusProtobuf
                    }
                    serviceId = id
                    if (agenda && Object.keys(agenda).length !== 0) {
                      sale.serviceFields.serviceAgenda = agenda
                    }
                  }
                })
                .catch(() => {
                  return logAndReject(
                    ERROR_TYPES.LOAD_SERVICE_ABORTED_AGENDA_NOT_FOUND,
                    `Unable to load agenda for service ${serviceId}`,
                    sale
                  )
                })
                .then(() => {
                  if (serviceId) {
                    return servicesManager.getServiceByServiceId(serviceId)
                  }
                })
                .then(service => {
                  if (service) {
                    const { serviceItem } = service
                    // The service.notes[] contains the service item location.
                    // The last item in the array is the latest recorded location.
                    if (serviceItem) {
                      sale.serviceFields.serviceItem = {
                        ...sale.serviceFields.serviceItem,
                        itemDetails: serviceItem,
                      }
                    }
                    // check feature flag independent_service_line_items for independent service line items
                    const serviceLineItemsFF = vdFeatureManager.get(
                      'independent_service_line_items'
                    )
                    if (
                      serviceLineItemsFF &&
                      !isServiceStatusFinal(sale.serviceFields)
                    ) {
                      return logAndReject(
                        ERROR_TYPES.LOAD_SERVICE_ABORTED_INCOMPLETE,
                        'Incomplete service. Continue the sale from sales history to proceed.',
                        sale
                      )
                    }
                  }
                  return sale
                })
                .catch(error => {
                  switch (error.type) {
                    case ERROR_TYPES.LOAD_SERVICE_ABORTED_INCOMPLETE:
                      return logAndReject(
                        ERROR_TYPES.LOAD_SERVICE_ABORTED_INCOMPLETE,
                        error.message,
                        sale
                      )
                    default:
                      return logAndReject(
                        ERROR_TYPES.LOAD_SERVICE_ABORTED_SERVICE_ITEM_NOT_FOUND,
                        `Unable to load item for service ${serviceId}`,
                        sale
                      )
                  }
                })
            }
            return sale
          } else {
            return logAndReject(
              ERROR_TYPES.LOAD_SALE_ABORTED_NOT_FOUND,
              `No sale with ID ${saleId} found.`
            )
          }
        })
    },
  }
}
